import { session } from 'feature/widget/SessionStore'
import { try_on } from 'feature/widget/TryOnStore'
import { ym } from 'feature/yama'
import { useLatestCallback } from 'hook/useLatestCallback'
import { employeeModal, EmployeeModalStore } from 'page/catalog/employee/EmployeeModal/EmployeeModalStore'
import { employee } from 'page/catalog/employee/store/EmployeeStore'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { elements } from 'page/catalog/store/elements/ElementsStore'
import { itemPreview } from 'page/catalog/store/ItemPreviewStore'
import { user } from 'page/catalog/store/UserStore'
import { userModal, UserModalStore } from 'page/catalog/UserModal/store/UserModalStore'
import { useEffect, useRef, useState } from 'react'
import { delay } from 'util/promise'

interface UseTryOnOptions {
  itemId: string
  skipBoutiqueCheck?: boolean // Для стенда можно пропустить проверку бутика
}

export function useTryOn({ itemId, skipBoutiqueCheck = false }: UseTryOnOptions) {
  const { boutique, basic, data } = useDemo()
  const [loading, setLoading] = useState(false)
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const shouldClosePreviewRef = useRef<boolean>(false) // Нужно ли закрывать превью после загрузки
  const windowStateRef = useRef<'none' | 'min' | 'max'>('none') // Текущее состояние окна виджета
  const MIN_LOADING_TIME = 2000 // Минимальное время показа лоадера в мс

  const finishLoading = useLatestCallback(() => {
    setLoading(false)
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
    // Закрываем превью товара только если оно было открыто при нажатии кнопки
    if (shouldClosePreviewRef.current) {
      itemPreview.close()
      shouldClosePreviewRef.current = false
    }
    startTimeRef.current = null
  })

  // Завершение лоадера с учётом минимального времени
  const finishLoadingWithMinTime = useLatestCallback(() => {
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current
      const remaining = MIN_LOADING_TIME - elapsed

      if (remaining > 0) {
        // Ждем оставшееся время до минимального
        loadingTimeoutRef.current = setTimeout(() => {
          finishLoading()
        }, remaining)
      } else {
        // Уже прошло достаточно времени
        finishLoading()
      }
    } else {
      // На всякий случай, если startTime не был установлен
      finishLoading()
    }
  })

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем, что сообщение от виджета
      if (event.data.source !== 'showoff-try-on') return
      if (try_on.currentItemId !== itemId) return

      const { name, message } = event.data

      if (name === 'on-identity-update' && session.started) {
        ym('session_identity_update', { session_id: session.session_id, item_id: itemId, ...message })
      }

      // Обработка события состояния виджета
      if (name === 'on-state') {
        const { window: windowState, screen } = message

        // Сохраняем текущее состояние окна
        windowStateRef.current = windowState

        // Если лоадер не активен, игнорируем дальнейшую обработку
        if (!startTimeRef.current) return

        const cancelLoading = ['profile', 'policy', 'profileSelect']

        // Если виджет открыт и экран profile, policy или profileSelect - убираем лоадер
        if (windowState === 'max' && cancelLoading.includes(screen)) {
          finishLoading()
          return
        }

        return
      }

      // Проверяем, что событие относится к этому товару
      if (message?.item_id !== itemId) return

      switch (name) {
        case 'on-generation-start':
          // Убираем лоадер только если виджет открыт
          if (windowStateRef.current === 'max') {
            finishLoadingWithMinTime()
          }
          break
        case 'on-generation-start-success':
          if (session.started) {
            ym('session_generation_start_success', { session_id: session.session_id, ...message })
          }
          // Убираем лоадер при успешном старте генерации (с учётом минимального времени)
          finishLoadingWithMinTime()
          break
        case 'on-generation-start-error':
          // Убираем лоадер при ошибке генерации СРАЗУ
          finishLoading()
          break
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [itemId, finishLoading, finishLoadingWithMinTime])

  const onTry = useLatestCallback(async () => {
    ym('click_try_on', { from: 'item_card', item_id: itemId })
    // Проверка бутика (если не пропущена)
    if (!skipBoutiqueCheck && boutique) {
      if (!employee.user) {
        employeeModal.open(new EmployeeModalStore())
        return
      }
      if (!user.present) {
        userModal.open(new UserModalStore(data))
        return
      }
    }

    // Запоминаем, открыта ли сейчас модалка товара
    const isPreviewOpen = itemPreview.optional?.item?.item_id === itemId
    shouldClosePreviewRef.current = isPreviewOpen

    // Показываем лоадер после прохождения проверок
    setLoading(true)
    startTimeRef.current = Date.now() // Запоминаем время начала

    // Fallback timeout на случай, если события не придут
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }
    loadingTimeoutRef.current = setTimeout(() => {
      finishLoading()
    }, Math.max(MIN_LOADING_TIME, 10000))

    elements.animateItem(itemId)
    if (basic) await delay(400)
    await try_on.open(itemId)
  })

  return {
    loading,
    onTry,
  }
}
